/**
 * @file PresetCard.tsx
 * @description Карточка заготовки в библиотеке «Добавить блок» (phase19.16
 * Р2; uiux-design-r2 §5): схема в цветах сайта, название, «для чего».
 * На ПК — плитка в сетке, на телефоне — строка (мини 96 + текст).
 * Недоступная заготовка видна, но выключена, причина — под названием.
 * Двойной клик вставляет сразу.
 */

import type { SectionPayload, SectionPreset } from "@chaiberi/shared";
import { useMemo } from "react";

import { PresetScheme } from "./PresetScheme";
import { cn } from "@/lib/utils";

interface Props {
  preset: SectionPreset;
  selected: boolean;
  blockedReason: string | null;
  onSelect: () => void;
  onInsert: () => void;
}

/** Схема из payload заготовки; сломалась сборка — карточка без схемы. */
function useScheme(preset: SectionPreset): SectionPayload | null {
  return useMemo(() => {
    try {
      return preset.build();
    } catch {
      return null;
    }
  }, [preset]);
}

export function PresetCard({ preset, selected, blockedReason, onSelect, onInsert }: Props) {
  const scheme = useScheme(preset);
  const disabled = blockedReason !== null;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      title={preset.title}
      onClick={() => (disabled ? undefined : onSelect())}
      onDoubleClick={() => (disabled ? undefined : onInsert())}
      className={cn(
        "flex min-h-24 w-full items-center gap-3 rounded-xl bg-surface-lowest p-2 text-left outline-none transition-colors sm:flex-col sm:items-stretch sm:p-3",
        "focus-visible:ring-2 focus-visible:ring-ring",
        selected ? "[box-shadow:inset_0_0_0_2px_var(--primary)]" : "[box-shadow:inset_0_0_0_1px_var(--border)] hover:bg-surface-low",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <span className="h-[60px] w-24 shrink-0 overflow-hidden rounded-lg [box-shadow:inset_0_0_0_1px_var(--outline-variant)] sm:aspect-[8/5] sm:h-auto sm:w-full">
        {scheme ? <PresetScheme payload={scheme} /> : <span className="flex h-full items-center justify-center bg-surface-low" />}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="type-body line-clamp-2 font-medium text-on-surface">{preset.title}</span>
        <span className="type-caption line-clamp-2 text-on-surface-variant">{blockedReason ?? preset.hint}</span>
      </span>
    </button>
  );
}
